import { uploadRequest, type UploadSuccess } from "@ano/shared/api/upload";
import { API_URL } from "../lib/config.js";

export interface UploadedAttachment {
  id: string;
  filename: string;
  file_type: string;
  file_size: number;
  file_category: string;
  storage_key: string;
  storage_url: string;
  thumbnail_url: string | null;
  width: number | null;
  height: number | null;
}

export interface PendingAttachment {
  id: string;
  file: File;
  previewUrl: string | null;
  status: "pending" | "uploading" | "uploaded" | "error";
  progress: number;
  uploaded?: UploadedAttachment;
  error?: string;
}

/**
 * Upload a single file to the server.
 * Returns the attachment metadata on success, or throws on failure.
 */
export async function uploadFile(
  file: File,
  token: string,
  onProgress?: (progress: number) => void,
): Promise<UploadedAttachment> {
  const formData = new FormData();
  formData.append("file", file);

  // Note: fetch doesn't support progress natively, but we simulate it
  // For true progress, XMLHttpRequest would be needed
  onProgress?.(10);

  const uploaded = (await uploadRequest({
    apiBaseUrl: API_URL,
    accessToken: token,
    formData,
  })) as UploadSuccess;

  onProgress?.(90);

  onProgress?.(100);
  return uploaded as UploadedAttachment;
}

/**
 * Create a preview URL for a file (for images).
 * Returns null for non-image files.
 */
export function createPreviewUrl(file: File): string | null {
  if (file.type.startsWith("image/")) {
    return URL.createObjectURL(file);
  }
  return null;
}

/**
 * Clean up a preview URL to free memory.
 */
export function revokePreviewUrl(url: string | null): void {
  if (url) {
    URL.revokeObjectURL(url);
  }
}
